import { ChevronLeft } from "lucide-react";
import type { ReactNode } from "react";

import { IgBorderColors } from "@intelligems/ig-design-tokens/constants";
import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgPaper } from "@intelligems/ig-theme/IgPaper";
import { Box, Stack, Tab, Tabs, useTheme } from "@mui/material";

import { useAlertBannerHeight } from "@src/providers/layout/useAlertBanner";

/**
 * The least height the active tab's body keeps: enough canvas to read a graph,
 * or a form's first fields. Below it the frame scrolls instead.
 */
import { WORKSPACE_BODY_MIN_HEIGHT } from "./workspaceBodyMinHeight";

import type { WorkspacePageFrameProps } from "./WorkspacePageFrameProps";

/**
 * The merchant-facing chrome around an embedded {@link CodegenWorkspace}.
 *
 * The code workspace is a developer surface; this frame is what makes it *a
 * tab* of something a merchant can read — a named entity with a description,
 * one save action, and a way back. It is deliberately domain-agnostic: it takes
 * slots, never a view model, so a rule and a shipping provider can wear the
 * same page without the frame knowing which is which.
 */
export const WorkspacePageFrame = <TabKey extends string>({
  backLabel,
  onBack,
  headerStatus,
  title,
  meta,
  summary,
  actions,
  tabs,
  selectedTab,
  onSelectedTabChange,
  children,
  banners,
  notice,
  embedded = false,
}: WorkspacePageFrameProps<TabKey>) => {
  const theme = useTheme();
  const alertBannerHeight = useAlertBannerHeight();
  const Surface = embedded ? Box : IgPaper;

  return (
    <Stack
      sx={{
        ...(embedded
          ? { flex: 1 }
          : { height: `calc(100dvh - ${alertBannerHeight}px)` }),
        minHeight: 0,
        width: "100%",
      }}
      spacing={1}
    >
      {banners}

      <Surface
        sx={{
          containerName: "workspace-page-frame",
          containerType: "inline-size",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          // The frame's one scroll region, for a viewport too short to hold
          // the header over the body's floor (below). Clipping instead left the
          // lower header and the body unreachable, with nothing to scroll.
          overflowX: "hidden",
          overflowY: "auto",
          p: 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            borderBottom: `1px solid ${theme.igBorders.core.disabled}`,
            px: 3,
            pt: 2,
            pb: 0.75,
          }}
        >
          <IgButton
            type="button"
            variant="text"
            color="secondary"
            startIcon={<ChevronLeft size={16} />}
            onClick={onBack}
            sx={{
              borderRadius: "6px",
              fontSize: "14px",
              height: 36,
              lineHeight: 1.5,
              minHeight: 36,
              px: 3,
              py: 0,
              "& .MuiButton-startIcon": {
                ml: 0,
                mr: 2,
              },
            }}
          >
            {backLabel}
          </IgButton>
          {headerStatus}
        </Stack>

        <Stack
          sx={{
            px: 8,
            pt: 6,
            "@container workspace-page-frame (max-width: 700px)": {
              px: 3,
              pt: 3,
            },
          }}
        >
          {/* The summary sits in its own row below the title/actions pair
              rather than inside the title column, so a description paragraph
              gets the frame's full width instead of the width left over next
              to the action buttons. */}
          <Stack spacing={3} sx={{ pb: 4 }}>
            <Stack
              justifyContent="space-between"
              sx={{
                alignItems: "flex-start",
                flexDirection: "row",
                gap: 5,
                "@container workspace-page-frame (max-width: 700px)": {
                  alignItems: "stretch",
                  flexDirection: "column",
                  gap: 3,
                },
              }}
            >
              <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
                {title}
                {meta}
              </Stack>
              {actions ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={4}
                  sx={{
                    flexShrink: 0,
                    "@container workspace-page-frame (max-width: 700px)": {
                      alignSelf: "flex-end",
                    },
                  }}
                >
                  {actions}
                </Stack>
              ) : null}
            </Stack>
            {summary}
          </Stack>

          {/* 16px under the summary (its row's own padding) and 16px above
              the tabs. */}
          {notice ? <Box sx={{ pb: 4 }}>{notice}</Box> : null}

          {/* 40px tabs: no block padding, 16px inline, 14px labels, the
              selected one semibold over the Border/Accent/Active underline. */}
          <Tabs
            value={selectedTab}
            onChange={(_event, next: TabKey) => onSelectedTabChange(next)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 40,
              "& .MuiTab-root": {
                minHeight: 40,
                height: 40,
                py: 0,
                px: 4,
                fontSize: "14px",
              },
              "& .MuiTab-root.Mui-selected": { fontWeight: 600 },
              "& .MuiTabs-indicator": {
                backgroundColor: IgBorderColors.accent.active,
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.key} value={tab.key} label={tab.label} />
            ))}
          </Tabs>
        </Stack>

        {/* An explicit `minHeight` is load-bearing: the embedded workspace
            and the react-flow canvas both size themselves off this box, and a
            flex child defaults to its content's height rather than the row's.
            It is a floor rather than 0 so a short viewport scrolls the frame
            instead of squeezing the body — the canvas, a settings form — down
            to nothing beneath a header that cannot move. */}
        <Box
          sx={{
            borderTop: `1px solid ${theme.igBorders.core.default}`,
            display: "flex",
            flex: 1,
            flexDirection: "column",
            flexShrink: 0,
            minHeight: WORKSPACE_BODY_MIN_HEIGHT,
          }}
        >
          {children}
        </Box>
      </Surface>
    </Stack>
  );
};
